import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

export default function Reset() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [email, setEmail] = useState(params.get("email") || "");
  const [token, setToken] = useState(params.get("token") || "");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!params.get("token")) toast.error("Missing reset token");
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return toast.error("Passwords don't match");
    if (pw.length < 6) return toast.error("Password must be 6+ characters");
    setBusy(true);
    try {
      await api.post("/auth/reset", { email, reset_token: token, new_password: pw });
      setDone(true);
      toast.success("Password updated");
      setTimeout(() => nav("/login"), 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed — the link may be expired.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="max-w-md mx-auto w-full px-6 py-10">
        {done ? (
          <div className="mt-10 text-center">
            <CheckCircle2 className="mx-auto text-emerald-500" size={56} strokeWidth={1.5} />
            <h1 className="mt-6 font-manrope text-2xl font-bold text-slate-900">Password updated</h1>
            <p className="text-slate-500 text-sm mt-2">Redirecting to login…</p>
          </div>
        ) : (
          <>
            <h1 className="mt-4 font-manrope text-3xl font-extrabold tracking-tight text-slate-900">Choose a new password</h1>
            <p className="text-slate-500 text-sm mt-2">Set the password you'd like to use going forward.</p>
            <form onSubmit={submit} className="mt-8 space-y-4">
              <input data-testid="reset-email-input" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-slate-900" />
              <input data-testid="reset-token-input" required value={token} onChange={(e) => setToken(e.target.value)} placeholder="Reset token (from email)" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-slate-900 font-mono text-xs" />
              <input data-testid="reset-password-input" required type="password" minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password (6+ chars)" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-slate-900" />
              <input data-testid="reset-password2-input" required type="password" minLength={6} value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder="Confirm password" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-slate-900" />
              <button data-testid="reset-submit-btn" disabled={busy} className="w-full bg-slate-900 text-white rounded-full py-4 font-semibold hover:bg-slate-800 active:scale-[0.98] transition-transform duration-150 disabled:opacity-60">{busy ? "Updating…" : "Update password"}</button>
            </form>
            <div className="mt-6 text-center">
              <Link to="/login" className="text-sm text-slate-500 hover:text-slate-900 transition-colors">← Back to login</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
